import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { colors } from '../theme';
import RevealOnScroll from './RevealOnScroll';

const AboutSection: React.FC = () => {
  return (
    <Box
      id="sobre"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: colors.blackMatte,
        backgroundImage: `
          linear-gradient(
            135deg,
            ${colors.blackMatte} 0%,
            ${colors.darkWine}15 50%,
            ${colors.blackMatte} 100%
          )
        `,
        position: 'relative',
        overflow: 'hidden',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C2A14C' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          opacity: 0.4,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <RevealOnScroll>
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
            <Typography
              variant="h2"
              component="h2"
              sx={{
                color: colors.beige,
                mb: 2,
                fontWeight: 700,
              }}
            >
              Sobre a Barba Barão
            </Typography>
            <Box
              sx={{
                width: '80px',
                height: '3px',
                backgroundColor: colors.agedGold,
                mx: 'auto',
              }}
            />
          </Box>
        </RevealOnScroll>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 4, md: 6 },
          }}
        >
          <Box sx={{ flex: 1 }}>
            <RevealOnScroll delay={100}>
              <Box
                sx={{
                  p: { xs: 3, md: 4 },
                  backgroundColor: `${colors.darkWine}20`,
                  border: `2px solid ${colors.agedGold}40`,
                  borderRadius: '4px',
                  height: '100%',
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: colors.agedGold,
                    mb: 3,
                    fontWeight: 700,
                  }}
                >
                  Nossa Essência
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: colors.beige,
                    lineHeight: 1.9,
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    mb: 2,
                  }}
                >
                  <strong>Barba forte, toque seco e presença marcante.</strong>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: colors.beige,
                    lineHeight: 1.9,
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    mb: 2,
                  }}
                >
                  Somos uma marca masculina old school, nascida para elevar o padrão de
                  cuidado pessoal nas barbearias brasileiras.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: colors.beige,
                    lineHeight: 1.9,
                    fontSize: { xs: '1rem', md: '1.1rem' },
                  }}
                >
                  Cada produto é desenvolvido com foco em <strong>qualidade profissional</strong>,
                  resultados reais e respeito à tradição do ofício.
                </Typography>
              </Box>
            </RevealOnScroll>
          </Box>

          <Box sx={{ flex: 1 }}>
            <RevealOnScroll delay={200}>
              <Box
                sx={{
                  p: { xs: 3, md: 4 },
                  backgroundColor: `${colors.darkWine}20`,
                  border: `2px solid ${colors.agedGold}40`,
                  borderRadius: '4px',
                  height: '100%',
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: colors.agedGold,
                    mb: 3,
                    fontWeight: 700,
                  }}
                >
                  Nosso Compromisso
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: colors.beige,
                    lineHeight: 1.9,
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    mb: 2,
                  }}
                >
                  <strong>Cuidado profissional de barbearia, sem frescura.</strong>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: colors.beige,
                    lineHeight: 1.9,
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    mb: 2,
                  }}
                >
                  Focamos em produtos que realmente funcionam. Nada de promessas vazias
                  ou ingredientes desnecessários.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: colors.beige,
                    lineHeight: 1.9,
                    fontSize: { xs: '1rem', md: '1.1rem' },
                  }}
                >
                  <strong>Produtos feitos para quem leva respeito no rosto.</strong>
                </Typography>
              </Box>
            </RevealOnScroll>
          </Box>
        </Box>

        <RevealOnScroll delay={300}>
          <Box
            sx={{
              mt: { xs: 6, md: 8 },
              p: { xs: 4, md: 6 },
              backgroundColor: `${colors.agedGold}15`,
              border: `3px solid ${colors.agedGold}`,
              borderRadius: '4px',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h3"
              sx={{
                color: colors.agedGold,
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '1.75rem', md: '2.5rem' },
              }}
            >
              Para Barbearias que Levam o Ofício a Sério
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: colors.beige,
                lineHeight: 1.8,
                maxWidth: '900px',
                mx: 'auto',
                fontSize: { xs: '1.1rem', md: '1.3rem' },
                fontWeight: 400,
              }}
            >
              Nosso foco está nas barbearias profissionais que buscam produtos de
              qualidade superior para seus clientes. Linguagem direta, resultados
              visíveis, parceria real.
            </Typography>
          </Box>
        </RevealOnScroll>
      </Container>
    </Box>
  );
};

export default AboutSection;
